import React from 'react';
import {
    Button,
    Group,
    Modal,
    Text,
    ButtonGroup,
    Badge,
    Box,
} from '@landr/maestro';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import { useUserCreditsContext } from 'contexts/UserCreditsContext/UserCreditsContext';
import { Trans, t } from '@lingui/macro';
import { DownloadType } from 'types/enums';
import { log } from 'utils/log';
import { DownloadableItem } from 'types';
import { ModalProps } from '../Modal.types';
import { EventIds } from '../../constants';
import * as Styled from './CreditsWarning.styled';

interface CreditsWarningContentProps extends ModalProps {
    downloadableItem: DownloadableItem | undefined;
    purchaseCostInCredits: number;
    userCredits: number | undefined;
    approveCreditUse: () => void;
}

interface CreditsWarningContentProps extends ModalProps {
    downloadableItem: DownloadableItem | undefined;
    handleConfirm: () => void;
    purchaseCostInCredits: number;
    userCredits: number | undefined;
}

export const CreditsWarningContent: React.FC<
    CreditsWarningContentProps & {}
> = ({
    downloadableItem,
    handleClose,
    handleConfirm,
    isOpen,
    purchaseCostInCredits,
    userCredits,
}) => {
    let processedSamplesWarning = null;

    if (
        (isOpen && downloadableItem?.type === DownloadType.Creator) ||
        downloadableItem?.type === DownloadType.CreatorMulti
    ) {
        processedSamplesWarning = (
            <Trans id="You're downloading processed samples. Unprocessed samples will be available on your Downloads page."></Trans>
        );
    }

    if (isOpen && !downloadableItem) {
        log.error(
            `Open CreditsWarningModal with falsy downloadableItem`,
            EventIds.FalsyDownloadableItem,
        );

        return null;
    }

    return (
        <>
            <Modal.Header>
                <Modal.Title>
                    <Trans>Use credits?</Trans>
                </Modal.Title>
            </Modal.Header>
            <Modal.Content>
                <Modal.Section>
                    <Group direction="vertical" mb="sm">
                        {processedSamplesWarning && (
                            <Group.Item>
                                <Styled.InModalNotification mt="xl">
                                    <Styled.Icon>
                                        <Styled.InfoFilled />
                                    </Styled.Icon>
                                    <Styled.Content>
                                        {processedSamplesWarning}
                                    </Styled.Content>
                                </Styled.InModalNotification>
                            </Group.Item>
                        )}
                        <Box display="flex" mb="md" alignItems="center">
                            <Text fontWeight="bold">
                                <Trans>Your current balance</Trans>
                            </Text>
                            &nbsp;
                            <Badge
                                variant="primary"
                                content={t`${userCredits} credits`}
                                max={99999}
                                isRounded
                                size="md"
                                kind="subtle"
                            />
                        </Box>
                        <Text size="md">
                            <Trans>
                                {'Access your samples at any time in '}
                                <strong>Downloads</strong>.
                            </Trans>
                        </Text>
                    </Group>
                </Modal.Section>
            </Modal.Content>
            <Modal.Footer>
                <ButtonGroup>
                    <Styled.CloseButton
                        variant="secondary"
                        kind="minimal"
                        onClick={handleClose as () => void}
                    >
                        <Trans id="Cancel" />
                    </Styled.CloseButton>
                    <Button variant="primary" onClick={handleConfirm}>
                        <Trans>Yes, use {purchaseCostInCredits} credits</Trans>
                    </Button>
                </ButtonGroup>
            </Modal.Footer>
        </>
    );
};

const CreditsWarning: React.FC<ModalProps> = ({ isOpen, handleClose }) => {
    const {
        approveCreditUse,
        cancelPurchase,
        item: downloadableItem,
        purchaseCostInCredits,
    } = usePurchaseControllerContext();
    const { userCredits } = useUserCreditsContext();

    const handleCloseCreditWarningModal = () => {
        handleClose();
        cancelPurchase();
    };

    const handleConfirm = () => {
        approveCreditUse();
        handleClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCloseCreditWarningModal}
            size="sm"
        >
            <CreditsWarningContent
                approveCreditUse={approveCreditUse}
                downloadableItem={downloadableItem}
                handleClose={handleCloseCreditWarningModal}
                handleConfirm={handleConfirm}
                isOpen={isOpen}
                purchaseCostInCredits={purchaseCostInCredits}
                userCredits={userCredits}
            />
        </Modal>
    );
};

export default CreditsWarning;
